/**
 * useLiveWorkAreas.ts
 *
 * Live-querying hook for work areas in a workspace.
 * Re-renders automatically when any work area changes in IndexedDB.
 *
 * Replaces: useWorkAreaContext() from workAreaContext
 *
 * Usage:
 *   const { workAreas, getWorkArea, isLoading } = useLiveWorkAreas(workspaceId)
 */

"use client";

import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import { appDB } from "../db/client";
import type { WorkAreaRecord } from "../db/schema";

interface UseLiveWorkAreasReturn {
  // All work areas for this workspace — same as workAreaContext.workAreas
  workAreas: WorkAreaRecord[];

  // Quick lookup by ID
  getWorkArea: (workAreaId: string) => WorkAreaRecord | undefined;

  // Whether still loading from IDB on first mount
  isLoading: boolean;
}

/**
 * Returns all work areas for a workspace, live-updating from IndexedDB.
 */
export function useLiveWorkAreas(workspaceId: string): UseLiveWorkAreasReturn {
  const workAreas = useLiveQuery(
    () =>
      appDB.workAreas
        .where("workspaceId")
        .equals(workspaceId)
        .toArray(),
    [workspaceId]
  );

  const workAreaMap = useMemo(() => {
    const map = new Map<string, WorkAreaRecord>();
    if (!workAreas) return map;
    for (const wa of workAreas) {
      map.set(wa._id, wa);
    }
    return map;
  }, [workAreas]);

  const getWorkArea = (workAreaId: string): WorkAreaRecord | undefined =>
    workAreaMap.get(workAreaId);

  return {
    workAreas: workAreas ?? [],
    getWorkArea,
    isLoading: workAreas === undefined,
  };
}
